import { useEffect, useRef, useState } from 'react';
import { BottomSheet, Label, RadioButton, Stack, Text } from '@adc/parallax-component-library';
import i18n, { type TFuncKey } from 'Utilities/i18n';
import log from 'Utilities/log';
import { Button } from '../Button';

type OptStateType = null | 'accept' | 'decline';

type OptInOutModalProps = {
  handleUpdate: (state: OptStateType) => Promise<void>;
  previousState: OptStateType;
  isOpen: boolean;
  setIsOpen(bool: boolean): void;
  positiveOption: TFuncKey;
  negativeOption: TFuncKey;
};

type OptInOutProps = {
  handleConsent: (prop: string) => Promise<AuthSession | null>;
  initialState: OptStateType;
  optionStrings: {
    titleTag: TFuncKey;
    positiveTag: TFuncKey;
    negativeTag: TFuncKey;
    positiveOption: TFuncKey;
    negativeOption: TFuncKey;
  };
};

function OptInOutModal({
  previousState,
  negativeOption,
  positiveOption,
  isOpen,
  setIsOpen,
  handleUpdate,
}: OptInOutModalProps) {
  const [optState, setOptState] = useState<OptStateType>(previousState);
  const isUpdatedDisabled = previousState === optState;
  const isAccepted = optState === 'accept';
  const isDeclined = optState === 'decline';

  const ref = useRef<BottomSheet>(null);

  const handleClose = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen) {
      ref.current?.present();
    } else {
      ref.current?.dismiss();
    }
  }, [isOpen]);

  return (
    <BottomSheet
      ref={ref}
      enableDynamicSizing
      topNavigationProps={{
        title: i18n.t('Global.microcopy.common.status'),
        buttonStart: {
          element: i18n.t('Global.microcopy.common.cancel'),
          onPress: handleClose,
          label: i18n.t('Global.microcopy.common.cancel'),
        },
        buttonEnd: {
          element: i18n.t('Global.microcopy.common.save'),
          onPress: () => void handleUpdate(optState),
          label: i18n.t('Global.microcopy.common.save'),
          disabled: isUpdatedDisabled,
        },
      }}
      onBackdropClick={handleClose}
      onDismiss={handleClose}
    >
      <Stack flexDirection="column" gap="$4">
        <RadioButton
          selected={isAccepted}
          label={i18n.t(positiveOption)}
          onPress={() => {
            setOptState('accept');
          }}
        />
        <RadioButton
          selected={isDeclined}
          label={i18n.t(negativeOption)}
          onPress={() => {
            setOptState('decline');
          }}
        />
      </Stack>
    </BottomSheet>
  );
}

function OptInOut({ handleConsent, initialState, optionStrings }: OptInOutProps) {
  const [optState, setOptState] = useState<OptStateType>(initialState);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleUpdate = async (state: OptStateType) => {
    if (optState === state) {
      return;
    }

    try {
      await handleConsent(state as string);
      setOptState(state);
      setIsOpen(false);
    } catch (error) {
      log.error(error);
    }
  };

  const isAccepted = optState === 'accept';
  const { negativeOption, negativeTag, positiveOption, positiveTag, titleTag } = optionStrings;

  return (
    <Stack display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
      <Stack display="flex" flexDirection="row" alignItems="center" gap="$2">
        <Text type="$bodyBase.medium" color="$text.80" nativeID={titleTag}>
          {i18n.t(titleTag)}:
        </Text>
        {/* eslint-disable-next-line jsx-a11y/label-has-for */}
        <Text aria-label={isAccepted ? i18n.t(positiveTag) : i18n.t(negativeTag)}>
          <Label.Text testID={isAccepted ? positiveTag : negativeTag}>
            {isAccepted ? i18n.t(positiveTag) : i18n.t(negativeTag)}
          </Label.Text>
        </Text>
      </Stack>
      <Button
        variant="primary"
        label={i18n.t('Global.microcopy.common.update')}
        // @ts-expect-error Parallax missing type for medium type
        type="$bodyBase.medium"
        onPress={() => {
          setIsOpen(true);
        }}
      />

      <OptInOutModal
        negativeOption={negativeOption}
        positiveOption={positiveOption}
        handleUpdate={handleUpdate}
        previousState={optState}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
    </Stack>
  );
}

export { OptInOut };
